import React, { useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import "../../style/customers.css";
import "../../style/sales.css";
import "../../style/customerDetails.css";

import SaleIcon from "../../assets/icons/SaleIcon";
import ReturnIcon from "../../assets/icons/ReturnIcon";
import CreditIcon from "../../assets/icons/CreditIcon";
import DebitIcon from "../../assets/icons/DebitIcon";

function CustomerDetailsPrint() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasPrintedRef = useRef(false);
  const customer = location.state?.customer || null;
  const transactions = useMemo(
    () => location.state?.transactions || [],
    [location.state?.transactions]
  );
  const currentBalance = location.state?.currentBalance || 0;

  // Helper function to format balance
  const formatBalance = (balance) => {
    if (balance === null || balance === undefined) return "€0.00";
    const amount = parseFloat(balance);
    if (isNaN(amount)) return "€0.00";
    return `€${amount.toFixed(2)}`;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "";
    }
  };

  // Helper function to format date and time for header
  const formatDateTime = () => {
    const now = new Date();
    return now.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Auto-open print dialog when component loads
  useEffect(() => {
    if (customer && transactions.length > 0 && !hasPrintedRef.current) {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        window.print();
        hasPrintedRef.current = true;
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [customer, transactions]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate(`/customer/${customer?.id || ""}`);
  };

  // Show loading state if no customer or transactions
  if (!customer || transactions.length === 0) {
    return (
      <div className="print-invoice-container">
        <div className="print-invoice-header">
          <button className="print-back-btn" onClick={handleBack}>
            <ArrowLeft size={15} />
            Back to Customer Details
          </button>
        </div>
        <div className="loading-container">
          <p>
            No customer data available. Please export from the Customer Details
            page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="print-invoice-container">
      {/* Header with Print button */}
      <div className="print-invoice-header">
        <div></div>
        <div>
          <button className="print-print-btn" onClick={handlePrint}>
            <Printer size={16} />
            Print
          </button>
        </div>
      </div>

      {/* Account Statement Content */}
      <div className="print-invoice-content"
      style={{ maxWidth: "860px" }}
      >
        {/* Header Section */}
        <div
          className="print-invoice-info"
          style={{
            borderBottom: "1px solid #EAEEF2",
            marginBottom: "20px",
            paddingBottom: "1px",
          }}
        >
          <div
            className="print-invoice-id"
            style={{
              borderBottom: "none",
              fontSize: "18px",
              fontWeight: "600",
            }}
          >
            Account Statement
          </div>
          <div
            className="print-invoice-date"
            style={{ fontSize: "14px", fontWeight: "5   00", color: "#424A53" }}
          >
            {formatDateTime()}
          </div>
        </div>

        {/* Customer Name and Account Balance */}
        <div
          className="print-invoice-info-name"
          style={{ marginBottom: "10px", borderBottom: "none" }}
        >
          <div
            className="print-customer-name"
            style={{ fontSize: "14px", fontWeight: "400", color: "#424A53" }}
          >
            Customer Name:{" "}
            <span style={{ fontWeight: "500" }}>{customer.name || "--"}</span>
          </div>
          <div
            style={{
              marginLeft: "auto",
              fontSize: "14px",
              fontWeight: "400",
              color: "#424A53",
            }}
          >
            Account Balance:{" "}
            <span
              className={`balance-badge ${
                currentBalance >= 0 ? "positive" : "negative"
              }`}
            >
              {formatBalance(currentBalance || 0)}
            </span>
          </div>
        </div>

        {/* Finance History Table */}
        <div className="customers-table-wrapper">
          <div className="customer-table-responsive">
            <table className="customer-table">
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>INV/RTN/TRN ID</th>
                  <th>TYPE</th>
                  <th>DETAILS</th>
                  <th>CREDIT / DEBIT</th>
                  <th>BALANCE</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>
                      {formatDate(transaction.tnx_date || transaction.date)}
                    </td>
                    <td>
                      {transaction.tnx_id || transaction.invoiceId || "--"}
                    </td>
                    <td>
                      <div className="customer-details-transaction-type">
                        {transaction.type === "Sale" && (
                          <>
                            <SaleIcon />
                            <span>Sale</span>
                          </>
                        )}
                        {transaction.type === "Return" && (
                          <>
                            <ReturnIcon />
                            <span>Return</span>
                          </>
                        )}
                        {transaction.type === "Credit" && (
                          <>
                            <CreditIcon />
                            <span>Credit</span>
                          </>
                        )}
                        {transaction.type === "Debit" && (
                          <>
                            <DebitIcon />
                            <span>Debit</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      {transaction.type === "Sale" ? (
                        <div className="d-flex gap-5 flex-row">
                          <div className="d-flex flex-column">
                            <p
                              className="text-muted"
                              style={{ margin: 0, fontSize: "12px" }}
                            >
                              Bill Amount
                            </p>
                            <div
                              className="customer-details-amount"
                              style={{ fontSize: "14px" }}
                            >
                              €{transaction.billAmount?.toFixed(2) || "0.00"}
                            </div>
                          </div>
                          <div className="d-flex flex-column">
                            <p
                              className="text-muted"
                              style={{ margin: 0, fontSize: "12px" }}
                            >
                              Paid
                            </p>
                            <div
                              className="customer-details-amount positive"
                              style={{ fontSize: "14px" }}
                            >
                              €{transaction.paidAmount?.toFixed(2) || "0.00"}
                            </div>
                          </div>
                        </div>
                      ) : transaction.returnId ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                color: "#57606A",
                                fontSize: "14px",
                              }}
                            >
                              Return Device
                            </span>
                            <span
                              style={{
                                color: "#57606A",
                                fontSize: "14px",
                                fontWeight: "500",
                              }}
                            >
                              €{transaction.bill_amount || transaction.billAmount?.toFixed(2) || "0.00"}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                color: "#57606A",
                                fontSize: "14px",
                              }}
                            >
                              Exchanged Device
                            </span>
                            <span
                              style={{
                                color: "#D800B0",
                                fontSize: "14px",
                                fontWeight: "500",
                              }}
                            >
                              €{transaction.paid_amount || transaction.paidAmount?.toFixed(2) || "0.00"}
                            </span>
                          </div>
                        </div>
                      ) : (
                        transaction.note || transaction.details || "--"
                      )}
                    </td>
                    <td>
                      {transaction.type === "Credit" ||
                      transaction.type === "Sale" ? (
                        <div
                          className="customer-details-amount negative"
                          style={{ fontSize: "14px", fontWeight: "500" }}
                        >
                          - €{Math.abs(transaction.amount || 0).toFixed(2)}
                        </div>
                      ) : (
                        <div
                          className="customer-details-amount positive"
                          style={{ fontSize: "14px", fontWeight: "500" }}
                        >
                          + €{Math.abs(transaction.amount || 0).toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td>
                      <div
                        className={`balance-badge ${
                          (transaction.balance || 0) >= 0
                            ? "positive"
                            : "negative"
                        }`}
                      >
                        {formatBalance(transaction.balance || 0)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerDetailsPrint;
